import * as Actions from '../actions/milestoneActions';
import Milestone from '../../entities/milestone';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type MilestoneAction = { 
    type: string; 
    milestone: Milestone | null; 
    milestoneId: string | Milestone; 
    partial: boolean; 
    lastFetch: number; 
    milestones: Milestone[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Milestone> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Milestone[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Milestone> | null, firmId: string, userId: string, updates?: Milestone[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'milestone', 'milestones', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Milestone>} data
 * @param {Milestone[]} [updates]
 * @returns {ArrayMap<Milestone>}
 */
function updateStorage(data: ArrayMap<Milestone>, updates?: Milestone[]): ArrayMap<Milestone> {
	if (logging) console.debug('⮂ milestoneReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Milestone[]} [updates]
 * @returns {ArrayMap<Milestone>}
 */
function updateStorageNoReturn(updates?: Milestone[]): void {
	if (logging) console.debug('⮂ milestoneReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Milestone[]} [data]
 * @returns {ArrayMap<Milestone>}
 */
function createState(data?: Milestone[] | ArrayMap<Milestone>): ArrayMap<Milestone> {
	if (!data) return ArrayMapRegistry.clone<Milestone>('milestone', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Milestone>('milestone', data.map(e => e));
    else return ArrayMapRegistry.clone<Milestone>('milestone', data);
}

function milestones(state: ArrayMap<Milestone>, action: MilestoneAction): ArrayMap<Milestone> {
	if (!state) state = createState();

	let st: ArrayMap<Milestone>;

	switch (action.type) {
		// action.milestone - the new value of milestone with id action.id
		// action.milestoneId - id of the target, used if new value is null
		case Actions.MILESTONE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ milestoneReducer: FETCH_ONE_SUCCESS', action);

			if (action.milestone === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.milestoneId)) {
				st = state.clone();
				st.set(action.milestone);
				// state.clear();

				updateStorageNoReturn([action.milestone]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.milestones
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.milestones
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.milestones
			 !partial, !stateEmpty, fetchEmpty	-> D return milestones in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge milestones in state newer than those in action.milestones
		 */
		case Actions.MILESTONE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.milestones.length === 0;
			const fetched = action.milestones.map(milestone => new Milestone({...milestone, persisted: true}));

			if (logging) console.debug(`⮂ milestoneReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} milestones with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ milestoneReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ milestoneReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Milestone> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Milestone[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(milestone => {
					const current = st.get(milestone.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && milestone.status === 'D') {
							// Remove it from state
							toUpdate.push(milestone);
							st.delete(milestone);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (milestone.addedOn > current.addedOn) {
								toUpdate.push(milestone);
								st.set(milestone);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (milestone.status !== 'D') {
							toUpdate.push(milestone);
							st.set(milestone);
						}
						else if (logging) console.debug('⮂ milestoneReducer: partialMerge: skipping deleted milestone not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ milestoneReducer: empty: milestones', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ milestoneReducer: fullMerge: milestones', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localMilestone => {
                    // If this milestone has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localMilestone.persisted) {
                        const fetchedMilestone = st.get(localMilestone.id);
                        if (!fetchedMilestone || localMilestone.addedOn > fetchedMilestone.addedOn) st.set(localMilestone);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.MILESTONE_FETCH_ONE_FAILURE:
			console.error('MILESTONE_FETCH_ONE_FAILURE', action.milestoneId, action.err);

			// No update to storage required
			return state;

		case Actions.MILESTONE_FETCH_FAILURE:
			console.error('MILESTONE_FETCH_FAILURE', action.milestoneId, action.err);

			// No update to storage required
			return state;

		case Actions.MILESTONE_RESTORE: {
		    let milestone = new Milestone({...action.milestone});
            st = state.clone();

            const existing = st.get(milestone.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (milestone.addedOn >= existing.addedOn) {
                    st.set(milestone);
                    return updateStorage(st, [milestone]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (milestone.status === 'D') return state;

                st.set(milestone);
                return updateStorage(st, [milestone]);
            }
        }
            
		case Actions.MILESTONE_SAVE: {
		    // console.debug('⮂ MILESTONE_SAVE reducer', action.milestone?.id);
		    
			// clone the milestone
			let milestone = new Milestone({...action.milestone});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new milestone in the state
			// If this is a create, add to the queue
			const _milestone = st.get(milestone.id);

			if (!_milestone) st.set(milestone);
			else {
				if (milestone.addedOn >= _milestone.addedOn) st.set(milestone);
			}

			return updateStorage(st, [milestone]);
		}

		case Actions.MILESTONE_SAVE_MANY: {
		    if (logging) console.debug('⮂ MILESTONE_SAVE_MANY reducer', action.milestones.map(milestone => milestone.id).join(','));
		    
			// clone the milestones
			let toProcess = action.milestones.map(milestone => new Milestone(milestone));

			st = state.clone();
			// state.clear();

			const updates: Milestone[] = [];

			toProcess.forEach(milestone => {
				// Add or update the new milestone in the state
				// If this is a create, add to the queue
				const existing = st.get(milestone.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (milestone.status !== 'D') {
						st.push(milestone);
						updates.push(milestone);
					}
					else if (logging) console.debug('⮂ MILESTONE_SAVE_MANY: skipping deleted milestone not in state');
				}
				else {
					if (milestone.addedOn >= existing.addedOn) {
						st.set(milestone);
						updates.push(milestone);
					}
					else if (logging) console.debug('⮂ Skipping, existing milestone had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.MILESTONE_REMOVE: {
			// clone the milestone
			const milestone = new Milestone({...action.milestone});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(milestone);

			return updateStorage(st, [milestone]);
		}

		case Actions.MILESTONE_RESTORE_FAILURE:
		case Actions.MILESTONE_SAVE_FAILURE: {
		    console.warn('MILESTONE_SAVE_FAILURE reducer', action.milestone?.id);
		    if (!action.milestone?.id) {
				console.warn('MILESTONE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const milestone = ensure(Milestone, action.milestone);
			if (!milestone) {
				console.error('Could not find milestone after call to save');
				return state;
			}

			st = state.clone();

			const _milestoneRef = state.get(milestone.id);

			if (_milestoneRef && (_milestoneRef.addedOn === milestone.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _milestone = new Milestone({..._milestoneRef});
				_milestone.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_milestone.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('milestone', _milestone).then(r => {
						if (r.isErr()) console.error(`⮂ milestoneReducer: failed to save milestone with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ milestoneReducer: saved milestone with fatalError info');
					}).catch(err => console.error(`⮂ milestoneReducer: failed to save milestone with fatalError info: ${err.message}`));

				st.set(_milestone);
			}

			return updateStorage(st);
		}

		case Actions.MILESTONE_SAVE_MANY_FAILURE: {
		    console.warn('MILESTONE_SAVE_MANY_FAILURE reducer', action.milestones.map(milestone => milestone.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.MILESTONE_RESTORE_SUCCESS:
		case Actions.MILESTONE_SAVE_SUCCESS: {
			if (!action.milestone?.id) {
				console.warn('MILESTONE_SAVE_SUCCESS reducer');
                return state;
			}

			const milestoneRef = state.get(action.milestone?.id);
			if (!milestoneRef) {
				console.warn('Could not find milestone after call to save');
                return state;
			}

			st = state.clone();

			if (action.milestone.addedOn >= milestoneRef.addedOn) {
				st.set(action.milestone);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const milestone = new Milestone({...milestoneRef, persisted: true});
				st.set(milestone);
			}

			return updateStorage(st, [action.milestone]);
		}

		case Actions.MILESTONE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ MILESTONE_SAVE_MANY_SUCCESS reducer', action.milestones.map(milestone => milestone.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const milestones: Milestone[] = [];

			action.milestones.forEach(e => {
				const milestoneRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!milestoneRef) console.warn('Could not find milestone after call to save');
				else if (e.addedOn >= milestoneRef.addedOn) {
				    st.set(e);
				    milestones.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const milestone = new Milestone({...milestoneRef, persisted: true});
					st.set(milestone);
					milestones.push(milestone);
				}
			});

			return updateStorage(st, milestones);
		}

		case Actions.MILESTONE_REMOVE_FAILURE: {
		    console.warn('MILESTONE_REMOVE_FAILURE action', action.milestone?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.MILESTONE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.MILESTONE_RESTORE_MANY: {
			if (logging) console.debug('⮂ MILESTONE_RESTORE_MANY reducer', action.milestones.map(milestone => milestone.id).join(','));

			const toProcess = action.milestones.map(milestone => new Milestone({...milestone}));
			st = state.clone();

			const updates: Milestone[] = [];

			toProcess.forEach(milestone => {
				const existing = st.get(milestone.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (milestone.addedOn >= existing.addedOn) {
						st.set(milestone);
						updates.push(milestone);
					}
					else if (logging) console.debug('⮂ MILESTONE_RESTORE_MANY: skipping older version of milestone');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (milestone.status !== 'D') {
						st.set(milestone);
						updates.push(milestone);
					}
					else if (logging) console.debug('⮂ MILESTONE_RESTORE_MANY: skipping deleted milestone not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.MILESTONE_REMOVE_MANY: {
			if (logging) console.debug('⮂ MILESTONE_REMOVE_MANY reducer', action.milestones.map(milestone => milestone.id).join(','));

			const toProcess = action.milestones.map(milestone => new Milestone({...milestone}));
			st = state.clone();

			toProcess.forEach(milestone => {
				st.delete(milestone);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default milestones;